/**
 * Per-frame boat sample from the columnar store (green-field live map).
 *
 * Replaces `buildLiveReplayFramePoints` (row-array reverse-scan + enriched object per source) with a
 * binary-search + lerp over the typed-array ring buffers in `liveColumnarStore`. Position is linearly
 * interpolated; heading uses the sensor `Hdg_deg` (angular interp), then `Cog_deg`, then the motion tangent
 * between the two bracketing samples — mirroring `resolveLiveReplayDisplayHeading` priority.
 *
 * Every channel is bracketed over the rows that actually CARRY it (`bracketFinite`), not over the single row
 * that happens to precede the playhead. Ring rows are per exact source timestamp with absent channels stored
 * as NaN, so any channel sampling faster than GPS leaves the position columns full of holes; bracketing on
 * the raw row index meant a hole under the playhead returned no sample at all and the boat was left drawn at
 * its last good position while the track — which skips non-finite points — ran on to the newest fix.
 */

import { liveColumnarStore } from '../store/liveColumnarStore';
import { interpolateAngle, motionHeadingDeg } from './trackInterpolation';

export interface ColumnarBoatSample {
  lng: number;
  lat: number;
  hdg: number | undefined;
}

const LAT = 'Lat_dd';
const LNG = 'Lng_dd';
const HDG = 'Hdg_deg';
const COG = 'Cog_deg';

/** A row is usable for position only if it carries BOTH coordinates (they always arrive together). */
export const POSITION_CHANNELS: readonly string[] = [LAT, LNG];
const HDG_CHANNELS: readonly string[] = [HDG];
const COG_CHANNELS: readonly string[] = [COG];

function lerp(a: number, b: number, f: number): number {
  if (!Number.isFinite(a)) return b;
  if (!Number.isFinite(b)) return a;
  return a + (b - a) * f;
}

/** Interpolated {lng,lat,hdg} per source at `playheadMs`, read from columnar ring buffers. */
export function sampleColumnarFrame(ids: Set<number>, playheadMs: number): Map<number, ColumnarBoatSample> {
  const out = new Map<number, ColumnarBoatSample>();
  if (!Number.isFinite(playheadMs) || playheadMs <= 0) return out;

  for (const id of ids) {
    // Position: nearest rows that actually carry both coordinates, skipping holes on either side.
    const br = liveColumnarStore.bracketFinite(id, playheadMs, POSITION_CHANNELS);
    if (!br) continue;
    const { i0, i1, frac } = br;

    const lat0 = liveColumnarStore.valueAt(id, LAT, i0);
    const lng0 = liveColumnarStore.valueAt(id, LNG, i0);

    let lat = lat0;
    let lng = lng0;
    if (i1 !== i0) {
      lat = lerp(lat0, liveColumnarStore.valueAt(id, LAT, i1), frac);
      lng = lerp(lng0, liveColumnarStore.valueAt(id, LNG, i1), frac);
    }

    // Heading: sensor Hdg (angular interp) -> Cog -> motion tangent between the two position samples.
    // Hdg/Cog are bracketed over their OWN rows: reading them at the position indices threw away a
    // perfectly good heading whenever the two channels did not share a timestamp, and dropped the
    // display straight to the (jittery) motion tangent.
    let hdg: number | undefined;
    const hb = liveColumnarStore.bracketFinite(id, playheadMs, HDG_CHANNELS);
    if (hb) {
      const h0 = liveColumnarStore.valueAt(id, HDG, hb.i0);
      hdg = hb.i1 !== hb.i0
        ? interpolateAngle(h0, liveColumnarStore.valueAt(id, HDG, hb.i1), hb.frac)
        : h0;
    } else {
      const cb = liveColumnarStore.bracketFinite(id, playheadMs, COG_CHANNELS);
      if (cb) {
        const c0 = liveColumnarStore.valueAt(id, COG, cb.i0);
        hdg = cb.i1 !== cb.i0
          ? interpolateAngle(c0, liveColumnarStore.valueAt(id, COG, cb.i1), cb.frac)
          : c0;
      } else if (i1 !== i0) {
        hdg = motionHeadingDeg(
          lat0,
          lng0,
          liveColumnarStore.valueAt(id, LAT, i1),
          liveColumnarStore.valueAt(id, LNG, i1)
        );
      }
    }

    out.set(id, { lng, lat, hdg });
  }
  return out;
}
